/** The app shell: header (group chip + switcher) and the active screen.
 *
 * All components live in the light DOM inside one ShadowRoot attached to
 * the <scapescene-widget> host, so the single injected stylesheet reaches
 * everything. Components must NOT attach their own shadow roots.
 */

import { passGate } from "@starling-cloud/sturnia-workshop";

import { Route, router } from "./control/router";
import { client, PROBE_PATH } from "./data/client";
import { store } from "./store";

import "./components/scapescene-header";
import "./screens/screen_start";
import "./screens/screen_group";
import "./screens/screen_editor";
import "./screens/screen_film";
import "./screens/screen_final";

const SCREEN_TAGS: Record<Route["name"], string> = {
  start: "scapescene-start-screen",
  join: "scapescene-start-screen",
  group: "scapescene-group-screen",
  frame: "scapescene-editor-screen",
  film: "scapescene-film-screen",
  final: "scapescene-final-screen",
};

export class App {
  #root: HTMLElement;
  #unsubscribe: (() => void)[] = [];
  #currentTag = "";
  #currentScreen: HTMLElement | null = null;

  constructor(root: HTMLElement) {
    this.#root = root;
  }

  start(): void {
    this.#unsubscribe.push(router.onChange((route) => this.render(route)));
    router.start();
    void this.#boot();
  }

  /** Show the shared workshop password gate first when the backend
   * demands one (the API enforces it; the widget is a public file); the
   * store only initialises once the device is let in. */
  async #boot(): Promise<void> {
    await passGate({
      client,
      probePath: PROBE_PATH,
      host: this.#root,
      title: "Starling Storyboard",
    });
    void store.init();
  }

  stop(): void {
    for (const off of this.#unsubscribe) off();
    router.stop();
    store.stopPolling();
  }

  render(route: Route): void {
    // No active group -> everything except join deep-links goes to start.
    const membership = store.membership;
    if (!membership && route.name !== "start" && route.name !== "join") {
      router.navigate("/start");
      return;
    }

    const tag = SCREEN_TAGS[route.name];

    // Same screen, new params (frame 2 -> frame 3): update in place so
    // the filmstrip keeps its DOM and scroll momentum.
    if (tag === this.#currentTag && this.#currentScreen) {
      if (route.name === "frame") {
        this.#currentScreen.setAttribute(
          "position",
          String(route.position),
        );
      }
      return;
    }

    // Clear the previous screen but never the password gate, which sits
    // on top until the device is let in.
    for (const child of Array.from(this.#root.children)) {
      if (!child.classList.contains("sw-gate")) child.remove();
    }
    this.#currentTag = tag;

    const header = document.createElement("scapescene-header");
    this.#root.appendChild(header);

    const screen = document.createElement(tag);
    if (route.name === "frame") {
      screen.setAttribute("position", String(route.position));
    }
    if (route.name === "join") {
      screen.setAttribute("join-code", route.query.get("code") ?? "");
    }
    this.#currentScreen = screen;
    this.#root.appendChild(screen);
  }
}
